import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bike, CheckCircle2, LogOut, MapPin, Phone, Truck, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtGNF, STATUS_STYLES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";

const DELIVERY_ACTIONS = [
  { id: "En route", label: "Je pars livrer", icon: Truck, variant: "bg-brand" },
  { id: "Livrée", label: "Livré au client", icon: CheckCircle2, variant: "bg-emerald-600" },
  { id: "Échec de livraison", label: "Échec", icon: XCircle, variant: "bg-rose-600" },
];

export default function CourierSpace() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () =>
    api
      .get("/courier/deliveries")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const update = async (order, status) => {
    const note = status === "Échec de livraison" ? window.prompt("Motif de l'échec ?") || "" : "";
    try {
      await api.put(`/courier/deliveries/${order.id}`, { delivery_status: status, note });
      toast.success(`Livraison mise à jour : ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) return <p className="p-6 text-sm text-slate-500">Chargement de vos tournées…</p>;

  const active = data.orders.filter((o) => !["Livrée", "Annulée", "Retournée"].includes(o.status));
  const done = data.orders.filter((o) => ["Livrée", "Annulée", "Retournée"].includes(o.status));

  const Card = ({ o }) => (
    <div className="ds-card p-4" data-testid={`courier-delivery-${o.number}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-num text-sm font-bold text-slate-900">{o.number}</p>
          <p className="text-xs text-slate-500">{fmtDate(o.created_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={`ds-chip ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
          <span className="ds-chip border-slate-300 bg-slate-100 text-slate-700">{o.delivery_status}</span>
        </div>
      </div>

      <div className="mt-3 space-y-1 text-sm">
        <p className="font-semibold text-slate-900">{o.customer_name}</p>
        <a href={`tel:${o.phone}`} className="flex items-center gap-1.5 text-brand" data-testid={`courier-call-${o.number}`}>
          <Phone className="h-3.5 w-3.5" /> {o.phone}
        </a>
        <p className="flex items-start gap-1.5 text-slate-600">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {o.address} · {o.zone_name}
        </p>
        {o.note && <p className="text-xs text-slate-500">Note : {o.note}</p>}
      </div>

      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm">
        {o.items.map((it) => (
          <div key={it.id} className="flex justify-between gap-2">
            <span className="min-w-0 truncate text-slate-700">{it.quantity} × {it.product_name}</span>
            <span className="font-num shrink-0 text-slate-500">{fmtGNF(it.total)}</span>
          </div>
        ))}
        <div className="mt-2 flex justify-between border-t border-slate-200 pt-2">
          <span className="font-semibold text-slate-900">
            {o.payment_method === "cash_on_delivery" ? "À encaisser (espèces)" : `${o.payment_method_label} — ${o.payment_status}`}
          </span>
          <span className="font-num font-bold text-brand">{fmtGNF(o.total)}</span>
        </div>
      </div>

      {!["Livrée", "Annulée", "Retournée"].includes(o.status) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {DELIVERY_ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => update(o, a.id)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-white ${a.variant}`}
              data-testid={`courier-action-${a.id}-${o.number}`}
            >
              <a.icon className="h-3.5 w-3.5" /> {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-10" data-testid="courier-space">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand text-white">
            <Bike className="h-4 w-4" />
          </span>
          <div>
            <p className="font-display text-sm font-bold text-slate-900">{data.courier.name}</p>
            <p className="text-[10px] text-slate-500">
              Livreur EMPIRE-SHOP · {data.courier.zone_name || "toutes zones"}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/connexion");
              }}
              className="ds-btn-ghost border-rose-200 py-2 text-xs text-rose-600"
              data-testid="courier-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Quitter
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-5">
        <section>
          <h2 className="font-display text-xl font-bold text-slate-900">Tournée du jour ({active.length})</h2>
          <div className="mt-3 space-y-3">
            {active.length === 0 && <p className="ds-card p-6 text-center text-sm text-slate-500">Aucune livraison en cours.</p>}
            {active.map((o) => <Card key={o.id} o={o} />)}
          </div>
        </section>

        {done.length > 0 && (
          <section>
            <h2 className="font-display text-lg font-bold text-slate-900">Historique ({done.length})</h2>
            <div className="mt-3 space-y-3">
              {done.slice(0, 15).map((o) => <Card key={o.id} o={o} />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
